import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createCompetitiveSubject, updateCompetitiveSubject } from "../../redux/slices/competitiveSubjectSlice";
import { fetchActiveExams } from "../../redux/slices/examSlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";

const CompetitiveSubjectModal = ({ show, handleClose, editId = null }) => {
  const dispatch = useDispatch();
  const { competitiveSubjects, isLoading } = useSelector((state) => state.competitiveSubjects);
  const { activeExams } = useSelector((state) => state.exams);

  const [formData, setFormData] = useState({
    exam_id: "",
    subject_name: "",
    description: "",
    image_url: "",
    status: "Active",
  });

  const [imageFile, setImageFile] = useState(null);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show) {
      dispatch(fetchActiveExams());
    }
  }, [dispatch, show]);

  useEffect(() => {
    if (show) {
      if (editId) {
        const subject = competitiveSubjects.find((s) => s.id === editId);
        if (subject) {
          setFormData({
            exam_id: subject.exam_id || "",
            subject_name: subject.subject_name || "",
            description: subject.description || "",
            image_url: subject.image_url || "",
            status: subject.status || "Active",
          });
        }
      } else {
        setFormData({
          exam_id: "",
          subject_name: "",
          description: "",
          image_url: "",
          status: "Active",
        });
      }
      setImageFile(null);
      setValidated(false);
    }
  }, [editId, competitiveSubjects, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        e.target.value = "";
        return;
      }

      // Mandatory Dimension Validation: 2200 x 2000 Px
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width !== 2200 || img.height !== 2000) {
          toast.error(
            `Image dimensions must be exactly 2200 x 2000 pixels. (Detected: ${img.width}x${img.height})`
          );
          setImageFile(null);
          e.target.value = "";
        } else {
          setImageFile(file);
        }
        URL.revokeObjectURL(objectUrl);
      };
      img.onerror = () => {
        toast.error("Failed to load image for validation");
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
      };
      img.src = objectUrl;
    } else {
      setImageFile(null);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setFormData((prev) => ({ ...prev, image_url: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    try {
      if (editId) {
        await dispatch(updateCompetitiveSubject({ id: editId, data: formData, imageFile })).unwrap();
        toast.success("Subject updated successfully");
      } else {
        await dispatch(createCompetitiveSubject({ data: formData, imageFile })).unwrap();
        toast.success("Subject created successfully");
      }
      handleClose();
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>{editId ? "Edit Subject" : "Add Subject"}</Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Exam <span className="text-danger">*</span></Form.Label>
            <Form.Select
              required
              name="exam_id"
              value={formData.exam_id}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value="">Select Exam</option>
              {activeExams.map((exam) => (
                <option key={exam.id} value={exam.id}>{exam.exam_name}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">Please select an exam.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Subject Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              name="subject_name"
              value={formData.subject_name}
              onChange={handleChange}
              placeholder="e.g. History, Mathematics, General Awareness"
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid">Please provide a subject name.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter description"
              disabled={isLoading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select name="status" value={formData.status} onChange={handleChange} disabled={isLoading}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Subject Image</Form.Label>
            <div className="mb-2 p-2 rounded border border-danger" style={{ backgroundColor: "#fef2f2" }}>
              <p className="text-danger fw-bold mb-0 small">
                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                IMPORTANT NOTE: Image must be exactly 2200 x 2000 pixels.
              </p>
            </div>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              disabled={isLoading}
            />
            {(imageFile || formData.image_url) && (
              <div className="mt-3 p-2 border rounded d-flex align-items-center gap-3">
                <img
                  src={imageFile ? URL.createObjectURL(imageFile) : formData.image_url}
                  alt="Preview"
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="flex-grow-1 overflow-hidden">
                  <small className="text-muted d-block text-truncate">
                    {imageFile ? imageFile.name : "Current Image"}
                  </small>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemoveImage}
                  disabled={isLoading}
                >
                  <FaRegTrashAlt />
                </Button>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="dark" type="submit" disabled={isLoading}>
            {isLoading ? <Spinner animation="border" size="sm" /> : editId ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CompetitiveSubjectModal;
